import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, TruncateTextComponent } from "@tylertech/common-components-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, ProfileSettingViewModel } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "human-resources-employee-task-completion-chart",
    templateUrl: "./humanResourcesEmployeeTaskCompletionChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class HumanResourcesEmployeeTaskCompletionChartComponent
    implements OnDestroy, OnInit
{
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartService = inject(ChartService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $commonDialogsService = inject(CommonDialogsService);

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private _chartCriteria: IChartCriteria;
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"pie">;

    isLoaded: boolean;
    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.ChartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get ChartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartTitle(): string
    {
        const parameter = this.getParameterById("SelectTask");
        if (!parameter) return "Employee Task Completion";
        const options = parameter.Options?.filter(x => x.Value === parameter.Value);
        const taskName = options.length === 0 ? "" : options[0].Display;
        return "Employee Task Completion: " + taskName;
    }

    get ChartSubtitle(): string
    {
        return "";
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private getParameterById(parameterId: string): ProfileSettingViewModel | undefined
    {
        const parameters = this.chartCriteria?.Parameters?.find(g => g.Id === parameterId);
        return parameters ? parameters : undefined;
    }

    hasData(): boolean
    {
        return (
            !Utilities.IsNullOrUndefined(this.chartCriteria?.ChartDatas) &&
            !Utilities.IsNullOrUndefined(this.chartCriteria.ChartDatas[0].Data[0]) &&
            this.chartCriteria.ChartDatas[0].Data[0].length > 0
        );
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "pie",
            options: {
                plugins: {
                    legend: {
                        display: true,
                        position: "right",
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"pie">[]) =>
                            {
                                return this.chartCriteria.ChartDatas[0].Labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<"pie">): string | string[] =>
                            {
                                return tooltipItem.formattedValue;
                            },
                        },
                    },
                },
                maintainAspectRatio: true,
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element)
                    {
                        return;
                    }

                    const index = element.index;
                    const statusNameAtIndex = this.chartCriteria.ChartDatas[0].Labels[index];
                    const parameter = this.getParameterById("SelectTask");
                    const options = parameter.Options.filter(x => x.Value === parameter.Value);
                    const taskName = options.length === 0 ? "" : options[0].Display;
                    this.$commonDialogsService.showAlertMessage(
                        this.chartCriteria.ChartDatas[0].Additional[0][statusNameAtIndex].join("</br>"),
                        `Employees with ${taskName}: ${statusNameAtIndex}`);
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        data: this.chartCriteria.ChartDatas[0].Data[0],
                        backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data[0].length),
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
